import { Account, AccountIn, AccountUpdateIn } from '@/client'
import { db } from '../database'
import { createUpdateSet, deepUnwrap } from '../util'
import { CacheStateRepository, CacheStateTable } from '../cacheState.store'
import dayjs from 'dayjs'
import {
  deserializePerson,
  PersonTable,
  serializePerson,
  createPersonTable,
} from './person.models'
import {
  _deletePerson,
  _getPerson,
  _getPersons,
  _patchPerson,
  _postPerson,
} from './person.requests'
import { applyPersonFilters, PersonFilters } from './person.filter'
import { sql } from 'kysely'

const lastCacheChange = ref<Date>()
const loadingFullReload = ref<boolean>(false)

type Pagination = {
  limit?: number
  offset?: number
}

type Sort = {
  column?: keyof PersonTable
  direction?: 'asc' | 'desc'
}

export type EnrichedAccount = Account & {
  application_count: number
}

export const PersonStore = {
  loadingFullReload,
  lastCacheChange,

  async bulkInsertPersons(accounts: Account[], batchSize: number = 1000) {
    const serializedPersons = accounts.map(serializePerson)

    await db.transaction().execute(async (trx) => {
      for (let i = 0; i < serializedPersons.length; i += batchSize) {
        const batch = serializedPersons.slice(i, i + batchSize)
        await trx
          .insertInto('person')
          .values(batch)
          .onConflict((oc) =>
            oc.column('_id').doUpdateSet(createUpdateSet(serializedPersons[0]))
          )
          .execute()
      }
    })
    if (accounts.length > 0) {
      lastCacheChange.value = new Date()
    }
    return accounts
  },

  async getAllPersons(): Promise<Account[]> {
    const records = await db.selectFrom('person').selectAll().execute()
    return records.map(deserializePerson)
  },

  async getPersonsCount(
    filters: PersonFilters = { isDeleted: false }
  ): Promise<number> {
    const unwrappedFilters = deepUnwrap(filters)

    let query = db.selectFrom('person').select(db.fn.count('_id').as('count'))
    query = applyPersonFilters(query, unwrappedFilters)

    const result = await query.executeTakeFirst()
    return result ? Number(result.count) : 0
  },

  async deleteAllPersons(): Promise<void> {
    await db.deleteFrom('person').execute()
    lastCacheChange.value = new Date()
  },

  getDatabase() {
    return db.selectFrom('person')
  },

  async refreshPersonCacheFromApi(
    filter: PersonFilters = {},
    includeDeleted: boolean = false
  ) {
    const results = await _getPersons({
      updatedGte: filter.updatedAfter,
      updatedLt: filter.updatedBefore,
      deletedAtGte: filter.deletedAfter,
      includeDeleted: includeDeleted,
    })

    if (!results.error && results.data) {
      await this.bulkInsertPersons(results.data)
    }
    return results
  },

  async updatePersonCacheFromApi(fullReload: boolean = false) {
    let isFullReload = true

    const lastRefresh = await CacheStateRepository.getCacheStateById('persons')

    const filter: PersonFilters = {}
    if (lastRefresh && lastRefresh.lastUpdate && !fullReload) {
      filter.updatedAfter = lastRefresh.lastUpdate
      isFullReload = false
    }

    loadingFullReload.value = isFullReload

    // Update updated licenses
    await this.refreshPersonCacheFromApi(filter)

    // Update deleted licenses
    if (lastRefresh && lastRefresh.lastUpdate && !fullReload) {
      await this.refreshPersonCacheFromApi(
        {
          deletedAfter: lastRefresh?.lastUpdate,
        },
        true // includeDeleted
      )
    }

    type UpdateCacheStateTable = Partial<Omit<CacheStateTable, 'id'>> & {
      id: string
    }
    const update: UpdateCacheStateTable = {
      id: 'persons',
      lastUpdate: dayjs().toISOString(),
    }

    if (isFullReload) {
      update.lastCompleteLoad = dayjs().toISOString()
    }

    await CacheStateRepository.upsertCacheState(update)
    loadingFullReload.value = false
  },

  async getPersons<T>(
    filters: PersonFilters = { isDeleted: false },
    pagination: Pagination = {},
    sort: Sort = {},
    join: boolean
  ): Promise<T[]> {
    let query = db.selectFrom('person').selectAll()

    query = applyPersonFilters(query, filters)

    if (join) {
      query = query
        .leftJoin('licence_out', 'licence_out.account_id', 'person._id')
        .selectAll('person')
        .select([
          sql<number>`count(distinct licence_out.software_id)`.as(
            'application_count'
          ),
          sql<number>`
        count(
          distinct case when licence_out.status <> 'disabled'
          then licence_out.software_id end
        )
      `.as('active_applications_count'),
        ])
        .groupBy('person._id')
    }

    if (pagination.limit) {
      query = query.limit(pagination.limit)
    }
    if (pagination.offset) {
      query = query.offset(pagination.offset)
    }
    if (sort.column) {
      query = query.orderBy(sort.column, sort.direction ?? 'asc')
    }

    const results = await query.execute()

    return results.map(deserializePerson) as T[]
  },

  async createPerson(newPerson: AccountIn) {
    return _postPerson(newPerson).then(async (response) => {
      if (!response.error && response.data) {
        const serializedPerson = serializePerson(response.data)
        await db.insertInto('person').values(serializedPerson).execute()
        lastCacheChange.value = new Date()
      }
      return response
    })
  },

  async getPersonById(id: string): Promise<Account | null> {
    const record = await db
      .selectFrom('person')
      .selectAll()
      .where('person._id', '=', id)
      .executeTakeFirst()

    let result = record ? deserializePerson(record) : record

    if (!record) {
      await _getPerson(id).then((response) => {
        if (response && response.data != null) {
          result = response.data
          this.bulkInsertPersons([response.data])
        }
      })
    }

    return result || null
  },

  async getPersonByEmail(email: string): Promise<Account | null> {
    const record = await db
      .selectFrom('person')
      .selectAll()
      .where('person.email', '=', email)
      .executeTakeFirst()

    const result = record ? deserializePerson(record) : record

    if (!record) {
      return null
    }

    return result || null
  },

  async updatePerson(id: string, updates: AccountUpdateIn) {
    return await _patchPerson(updates).then(async (response) => {
      if (response.error) return response

      const serializedUpdates = serializePerson({
        ...response.data,
      } as Account)

      await db
        .updateTable('person')
        .set(serializedUpdates)
        .where('person._id', '=', id)
        .execute()

      lastCacheChange.value = new Date()
      return response
    })
  },

  async deletePerson(id: string) {
    return _deletePerson(id).then(async (response) => {
      await db.deleteFrom('person').where('person._id', '=', id).execute()
      lastCacheChange.value = new Date()
      return response
    })
  },

  usePersonsCount(
    initialFilters:
      | PersonFilters
      | Ref<PersonFilters>
      | ComputedRef<PersonFilters> = {}
  ) {
    const count = ref<number>(0)

    const fetchCount = async () => {
      const filters = deepUnwrap(initialFilters) as PersonFilters
      count.value = await this.getPersonsCount(filters)
    }

    const stopWatch = watch(
      [() => unref(initialFilters), lastCacheChange],
      async () => {
        await fetchCount()
      },
      { deep: true }
    )

    fetchCount()

    return {
      count,
      unsubscribe: stopWatch,
      lastCacheUpdate: lastCacheChange,
    }
  },

  usePersons<T>(
    initialFilters:
      | PersonFilters
      | Ref<PersonFilters>
      | ComputedRef<PersonFilters> = {},
    initialPagination:
      | Pagination
      | Ref<Pagination>
      | ComputedRef<Pagination> = {},
    initialSort: Sort | Ref<Sort> | ComputedRef<Sort> = {},
    join: boolean
  ) {
    const data = ref<T[]>([])
    const loading = ref<boolean>(false)

    const fetchData = async () => {
      loading.value = true
      const filters = deepUnwrap(initialFilters) as PersonFilters
      const pagination = deepUnwrap(initialPagination) as Pagination
      const sort = deepUnwrap(initialSort) as Sort

      data.value = await this.getPersons<T>(filters, pagination, sort, join)
      loading.value = false
    }

    const stopWatch = watch(
      [
        () => unref(initialFilters),
        () => unref(initialPagination),
        () => unref(initialSort),
        lastCacheChange,
      ],
      async () => {
        await fetchData()
      },
      { deep: true }
    )

    const updateParameters = (
      newFilters: Partial<PersonFilters> = {},
      newPagination: Partial<Pagination> = {},
      newSort: Partial<Sort> = {}
    ) => {
      const cloneAndMerge = <T extends object>(
        source: T | Ref<T>,
        updates: Partial<T>
      ): T => {
        if (isRef(source)) {
          return { ...source.value, ...updates }
        } else {
          return { ...source, ...updates }
        }
      }

      initialFilters = cloneAndMerge(initialFilters, newFilters)
      initialPagination = cloneAndMerge(initialPagination, newPagination)
      initialSort = cloneAndMerge(initialSort, newSort)

      fetchData()
    }

    fetchData()

    return {
      data,
      unsubscribe: stopWatch,
      updateParameters,
      lastCacheUpdate: lastCacheChange,
      loading,
    }
  },

  async reset() {
    await db.schema.dropTable('person').ifExists().execute()
    await createPersonTable()
    lastCacheChange.value = undefined
  },
}
